import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiUser,
  FiAward,
  FiMessageCircle,
  FiCheck,
  FiX,
  FiStar,
  FiTarget,
  FiClock,
} from 'react-icons/fi';
import Sidebar from '../../Sidebar';
import { getStoredUser } from '../../../utils/authSession';
import './TAGECoaching.css';

const TAGECoaching = () => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [modifiedPlan, setModifiedPlan] = useState(null);
  const [showModifications, setShowModifications] = useState(false);
  const [user, setUser] = useState(() => getStoredUser());

  // Mock TAGE MAGE coaches
  const tageCoaches = [
    {
      id: 'coach-1',
      name: 'Dr. Marie Dupont',
      title: 'Experte TAGE MAGE - HEC Paris',
      experience: '12 ans',
      specialties: ['Logique', 'Calcul', 'Expression'],
      score: 550,
      studentsHelped: 250,
      successRate: 92,
      bio: "Docteure en mathématiques appliquées, spécialisée dans la préparation aux concours d'entrée des grandes écoles. Ancienne étudiante HEC Paris avec un score de 550/600 au TAGE MAGE.",
      availability: 'Lundi, Mercredi, Vendredi',
      hourlyRate: '50€/h',
      rating: 4.9,
    },
    {
      id: 'coach-2',
      name: 'Thomas Bernard',
      title: 'Coach TAGE MAGE - ESSEC',
      experience: '8 ans',
      specialties: ['C.D.T.', 'R&A', 'Conditions Minimales'],
      score: 520,
      studentsHelped: 180,
      successRate: 88,
      bio: "Diplômé ESSEC Business School, spécialisé dans les sections verbales du TAGE MAGE. Plus de 8 ans d'expérience dans l'accompagnement des candidats.",
      availability: 'Mardi, Jeudi, Samedi',
      hourlyRate: '45€/h',
      rating: 4.8,
    },
    {
      id: 'coach-3',
      name: 'Sophie Martin',
      title: 'Spécialiste TAGE MAGE - ESCP',
      experience: '10 ans',
      specialties: ['Toutes sections', 'Stratégie globale'],
      score: 540,
      studentsHelped: 300,
      successRate: 94,
      bio: "Ancienne directrice de préparation aux concours, spécialisée dans l'approche globale du TAGE MAGE. Connue pour ses méthodes efficaces et personnalisées.",
      availability: 'Tous les jours',
      hourlyRate: '60€/h',
      rating: 5.0,
    },
  ];

  useEffect(() => {
    // Get plan from enrollment cache instead of old localStorage key
    const currentUser = getStoredUser();
    if (currentUser?.email) {
      const enrollmentCache = localStorage.getItem(
        `tage_enrollment_${currentUser.email}`
      );
      if (enrollmentCache) {
        try {
          const enrollmentData = JSON.parse(enrollmentCache);
          if (enrollmentData?.study_plan) {
            setSelectedPlan(enrollmentData.study_plan);
          }
        } catch (error) {
          console.error('Error parsing enrollment cache:', error);
        }
      }
    }
  }, []);

  // Handle coach selection
  const handleSelectCoach = (coach) => {
    setSelectedCoach(coach);
  };

  // Simulate coach modifying the plan
  const handleRequestModification = () => {
    if (selectedCoach && selectedPlan) {
      // Simulate coach modification (in real app, this would be via messaging)
      const modified = {
        ...selectedPlan,
        modifications: [
          {
            section: 'Phase 1',
            original: selectedPlan.phases[0].duration,
            modified: '1.5 mois',
            reason: 'Renforcement des fondamentaux recommandé',
          },
          {
            section: 'Fréquence',
            original: selectedPlan.frequency,
            modified: selectedPlan.frequency.includes('6')
              ? '5 fois par semaine'
              : selectedPlan.frequency,
            reason: 'Équilibre optimal entre intensité et récupération',
          },
        ],
        modifiedBy: selectedCoach.name,
      };
      setModifiedPlan(modified);
      setShowModifications(true);
    }
  };

  // Accept modifications
  const handleAcceptModifications = () => {
    // TODO: In future, call backend API to update study plan with modifications
    // For now, just store coach assignment
    localStorage.setItem('tage_assigned_coach', JSON.stringify(selectedCoach));

    // Update enrollment cache with modified plan
    const currentUser = getStoredUser();
    if (currentUser?.email) {
      const enrollmentCache = localStorage.getItem(
        `tage_enrollment_${currentUser.email}`
      );
      if (enrollmentCache) {
        try {
          const enrollmentData = JSON.parse(enrollmentCache);
          enrollmentData.study_plan = modifiedPlan;
          localStorage.setItem(
            `tage_enrollment_${currentUser.email}`,
            JSON.stringify(enrollmentData)
          );
        } catch (error) {
          console.error('Error updating enrollment cache:', error);
        }
      }
    }

    navigate('/exams/tage');
  };

  // Reject modifications
  const handleRejectModifications = () => {
    setShowModifications(false);
    setModifiedPlan(null);
  };

  // Skip coach and continue
  const handleSkipCoach = () => {
    navigate('/exams/tage');
  };

  return (
    <div className="layout">
      <Sidebar />
      <div className="content">
        <div className="tage-coaching-container">
          {/* Header */}
          <div className="tage-coaching-header">
            <h1>Choisissez votre Coach TAGE MAGE</h1>
            <p className="header-subtitle">
              Nos coachs experts vous accompagnent pour optimiser votre
              préparation et maximiser vos chances de réussite.
            </p>
          </div>

          {/* Current Plan Summary */}
          {selectedPlan && (
            <div className="current-plan-summary">
              <h3>
                <FiTarget size={20} /> Votre Plan Sélectionné
              </h3>
              <div className="plan-summary-content">
                <div className="plan-summary-item">
                  <span className="summary-label">Plan</span>
                  <span className="summary-value">{selectedPlan.name}</span>
                </div>
                <div className="plan-summary-item">
                  <span className="summary-label">Durée</span>
                  <span className="summary-value">{selectedPlan.duration}</span>
                </div>
                <div className="plan-summary-item">
                  <span className="summary-label">Fréquence</span>
                  <span className="summary-value">
                    {selectedPlan.frequency}
                  </span>
                </div>
                <div className="plan-summary-item">
                  <span className="summary-label">Heures/semaine</span>
                  <span className="summary-value">
                    {selectedPlan.hoursPerWeek}h
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Coaches Grid */}
          <div className="tage-coaches-section">
            <h3>Nos Coachs TAGE MAGE</h3>
            <div className="coaches-grid">
              {tageCoaches.map((coach) => (
                <div
                  key={coach.id}
                  className={`coach-card ${selectedCoach?.id === coach.id ? 'selected' : ''}`}
                  onClick={() => handleSelectCoach(coach)}
                >
                  <div className="coach-header">
                    <div className="coach-avatar">
                      <FiUser size={40} />
                    </div>
                    <div className="coach-rating">
                      <FiStar size={16} />
                      <span>{coach.rating}</span>
                    </div>
                  </div>

                  <div className="coach-info">
                    <h4>{coach.name}</h4>
                    <p className="coach-title">{coach.title}</p>
                  </div>

                  <div className="coach-stats">
                    <div className="stat-item">
                      <span className="stat-label">Score TAGE MAGE</span>
                      <span className="stat-value">{coach.score}/600</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Expérience</span>
                      <span className="stat-value">{coach.experience}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Étudiants aidés</span>
                      <span className="stat-value">
                        {coach.studentsHelped}+
                      </span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Taux de réussite</span>
                      <span className="stat-value">{coach.successRate}%</span>
                    </div>
                  </div>

                  <div className="coach-specialties">
                    <span className="specialties-label">Spécialités :</span>
                    <div className="specialties-tags">
                      {coach.specialties.map((specialty, index) => (
                        <span key={index} className="specialty-tag">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="coach-bio">
                    <p>{coach.bio}</p>
                  </div>

                  <div className="coach-details">
                    <div className="detail-row">
                      <FiClock size={16} />
                      <span>{coach.availability}</span>
                    </div>
                    <div className="detail-row">
                      <FiAward size={16} />
                      <span>{coach.hourlyRate}</span>
                    </div>
                  </div>

                  {selectedCoach?.id === coach.id && (
                    <div className="coach-selected-indicator">
                      <FiCheck size={20} /> Sélectionné
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Coach Actions */}
          {selectedCoach && !showModifications && (
            <div className="coach-actions">
              <p className="action-description">
                Souhaitez-vous demander à {selectedCoach.name} de réviser votre
                plan d'étude ?
              </p>
              <div className="action-buttons">
                <button className="btn-lwz-outline" onClick={handleSkipCoach}>
                  Continuer sans modification
                </button>
                <button
                  className="btn-lwz-primary"
                  onClick={handleRequestModification}
                >
                  <FiMessageCircle size={18} />
                  Demander une révision du plan
                </button>
              </div>
            </div>
          )}

          {/* Modifications Review */}
          {showModifications && modifiedPlan && (
            <div className="modifications-review">
              <div className="modifications-header">
                <h3>Modifications Proposées par {selectedCoach.name}</h3>
                <p>
                  Votre coach a analysé votre plan et propose les modifications
                  suivantes pour optimiser votre préparation.
                </p>
              </div>

              <div className="modifications-list">
                {modifiedPlan.modifications.map((mod, index) => (
                  <div key={index} className="modification-item">
                    <div className="modification-section">
                      <strong>{mod.section}</strong>
                    </div>
                    <div className="modification-change">
                      <div className="change-original">
                        <FiX size={16} className="icon-remove" />
                        <span>{mod.original}</span>
                      </div>
                      <div className="change-arrow">→</div>
                      <div className="change-new">
                        <FiCheck size={16} className="icon-add" />
                        <span>{mod.modified}</span>
                      </div>
                    </div>
                    <div className="modification-reason">
                      <em>Raison : {mod.reason}</em>
                    </div>
                  </div>
                ))}
              </div>

              <div className="modifications-actions">
                <button
                  className="btn-lwz-outline btn-reject"
                  onClick={handleRejectModifications}
                >
                  <FiX size={18} />
                  Refuser les modifications
                </button>
                <button
                  className="btn-lwz-primary btn-accept"
                  onClick={handleAcceptModifications}
                >
                  <FiCheck size={18} />
                  Accepter et commencer
                </button>
              </div>
            </div>
          )}

          {/* Skip Option */}
          {!selectedCoach && (
            <div className="skip-coach-option">
              <button className="btn-link" onClick={handleSkipCoach}>
                Passer cette étape et commencer directement →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TAGECoaching;
